import type {
  RfqAttachmentKind,
  RfqCategory,
  RfqPriceMode,
  RfqReplyStatus,
  RfqStatus,
} from "./types";

import type { RfqCalcLabels } from "./calc";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const CATEGORY_FALLBACK: Record<RfqCategory, string> = {
  material: "材料",
  fabrication: "工廠加工製作",
  surface: "表面處理",
  bolt: "螺栓/續接器",
  transport: "運輸/吊裝",
  other: "其他",
};

const STATUS_FALLBACK: Record<RfqStatus, string> = {
  draft: "草稿",
  inquiring: "詢價中",
  comparing: "比價中",
  pendingReview: "待主管審核",
  awarded: "已決標",
  voided: "作廢",
};

const PRICE_MODE_FALLBACK: Record<RfqPriceMode, string> = {
  weight: "重量 (元/kg)",
  unit: "單位",
};

const REPLY_FALLBACK: Record<RfqReplyStatus, string> = {
  pending: "未回覆",
  quoted: "已收到報價",
  declined: "婉拒",
  overdue: "逾期未回",
};

const ATTACH_KIND_FALLBACK: Record<RfqAttachmentKind, string> = {
  drawing: "圖說",
  spec: "規範",
  material: "材質要求",
  other: "其他",
};

const AWARD_REASON_FALLBACK: Record<string, string> = {
  dueDate: "交期無法配合",
  quality: "品質履約紀錄不良",
  capacity: "產能不足",
  noMtc: "無材證能力",
  altMaterial: "材質需替代",
  payment: "付款條件差異",
  other: "其他",
};

export function useRfqLabels() {
  const { t } = useTranslation();

  return useMemo(() => {
    const category = (value: RfqCategory) =>
      t(`rfq.category.${value}`) || CATEGORY_FALLBACK[value];
    const status = (value: RfqStatus) =>
      t(`rfq.status.${value}`) || STATUS_FALLBACK[value];
    const priceMode = (value: RfqPriceMode) =>
      t(`rfq.priceMode.${value}`) || PRICE_MODE_FALLBACK[value];
    const reply = (value: RfqReplyStatus) =>
      t(`rfq.reply.${value}`) || REPLY_FALLBACK[value];
    const attachKind = (value: RfqAttachmentKind) =>
      t(`rfq.attachKind.${value}`) || ATTACH_KIND_FALLBACK[value];
    const awardReason = (value: string) =>
      t(`rfq.awardReason.${value}`) || AWARD_REASON_FALLBACK[value] || value;

    const calc: RfqCalcLabels = {
      manualInput: t("rfq.calc.manualInput") || "人工輸入",
      manualOverride: t("rfq.calc.manualOverride") || "人工覆寫",
      quote: t("rfq.calc.quote") || "報價",
      lineWeight: t("rfq.calc.lineWeight") || "本項總重",
      freightShare: t("rfq.calc.freightShare") || "運費分攤",
      compareBase: t("rfq.calc.compareBase") || "比價基準",
      noWeight: t("rfq.calc.noWeight") || "無總重，無法換算",
      taxIncl: t("rfq.pricing.taxIncl") || "含稅",
      taxExcl: t("rfq.pricing.taxExcl") || "未稅",
      freightIncluded: t("rfq.calc.freightIncluded") || "含運",
      freightApportioned: t("rfq.calc.freightApportioned") || "加運費分攤",
      exw: t("rfq.pricing.freightExw") || "出廠",
      pieces: t("rfq.calc.pieces") || "支",
      perUnit: (unit: string) =>
        t("rfq.calc.perUnit", { defaultValue: "元/{{unit}}", unit }),
      unitMismatch: (quoteUnit: string, lineUnit: string) =>
        t("rfq.calc.unitMismatch", {
          defaultValue:
            "報價單位 {{quoteUnit}} 與詢價單位 {{lineUnit}} 不同，無法自動換算",
          quoteUnit,
          lineUnit,
        }),
      taxUpliftNote: t("rfq.calc.taxUplift") || "含稅 ×1.05",
    };

    return {
      t,
      category,
      status,
      priceMode,
      reply,
      attachKind,
      awardReason,
      calc,
    };
  }, [t]);
}
